import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Tag as TagIcon,
  Lock as LockIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  SelectAll as SelectAllIcon,
  DeselectOutlined as DeselectIcon
} from '@mui/icons-material';
import api from '../../../services/api';

// eslint-disable-next-line max-lines-per-function
function SlackChannelSelector({
  entityId,
  workspaceId,
  selectedChannels = [],
  onChannelSelection
}) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [channelTypeFilter, setChannelTypeFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      fetchChannels();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, entityId]);

  const fetchChannels = async () => {
    if (!workspaceId || !entityId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.listSlackChannels(entityId, workspaceId);
      setChannels(response.channels || []);
    } catch (err) {
      console.error('Failed to fetch Slack channels:', err);
      setError('Failed to load channels: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshChannels = async () => {
    setRefreshing(true);
    try {
      const response = await api.refreshSlackChannels(entityId, workspaceId);
      setChannels(response.channels || []);
    } catch (err) {
      console.error('Failed to refresh channels:', err);
      setError('Failed to refresh channels: ' + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleChannelToggle = (channel) => {
    const isSelected = selectedChannels.some(c => c.id === channel.id);
    let newSelection;

    if (isSelected) {
      newSelection = selectedChannels.filter(c => c.id !== channel.id);
    } else {
      newSelection = [...selectedChannels, channel];
    }

    if (onChannelSelection) {
      onChannelSelection(newSelection);
    }
  };

  const handleSelectAll = () => {
    if (onChannelSelection) {
      onChannelSelection(filteredChannels);
    }
  };

  const handleDeselectAll = () => {
    if (onChannelSelection) {
      onChannelSelection([]);
    }
  };

  const getChannelIcon = (channel) => {
    if (channel.isPrivate) {
      return <LockIcon fontSize="small" color="warning" />;
    }
    if (channel.isIM) {
      return <PersonIcon fontSize="small" color="info" />;
    }
    if (channel.isGroup) {
      return <GroupIcon fontSize="small" color="secondary" />;
    }
    return <TagIcon fontSize="small" color="primary" />;
  };

  const getChannelTypeLabel = (channel) => {
    if (channel.isPrivate) return 'Private';
    if (channel.isIM) return 'Direct Message';
    if (channel.isGroup) return 'Group';
    return 'Public';
  };

  const filteredChannels = channels.filter(channel => {
    const matchesSearch = channel.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         channel.purpose?.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;

    switch (channelTypeFilter) {
      case 'public':
        return !channel.isPrivate && !channel.isIM && !channel.isGroup;
      case 'private':
        return channel.isPrivate;
      case 'dm':
        return channel.isIM;
      case 'group':
        return channel.isGroup;
      default:
        return true;
    }
  });

  if (!workspaceId) {
    return (
      <Alert severity="warning">
        Please select a workspace first to view available channels.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Select Channels to Monitor
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Select all visible channels">
            <IconButton onClick={handleSelectAll} disabled={filteredChannels.length === 0}>
              <SelectAllIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deselect all channels">
            <IconButton onClick={handleDeselectAll} disabled={selectedChannels.length === 0}>
              <DeselectIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh channel list">
            <IconButton onClick={refreshChannels} disabled={refreshing}>
              {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search channels..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={channelTypeFilter}
            onChange={(e) => setChannelTypeFilter(e.target.value)}
            label="Type"
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="group">Groups</MenuItem>
            <MenuItem value="dm">Direct Messages</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Selection Summary */}
      {selectedChannels.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {selectedChannels.length} channel{selectedChannels.length !== 1 ? 's' : ''} selected
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedChannels.slice(0, 5).map((channel) => (
              <Chip
                key={channel.id}
                size="small"
                label={`#${channel.name}`}
                onDelete={() => handleChannelToggle(channel)}
              />
            ))}
            {selectedChannels.length > 5 && (
              <Chip
                size="small"
                label={`+${selectedChannels.length - 5} more`}
                variant="outlined"
              />
            )}
          </Box>
        </Alert>
      )}

      {/* Channel List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredChannels.length === 0 ? (
        <Alert severity="info">
          {searchQuery || channelTypeFilter !== 'all'
            ? 'No channels match your search criteria.'
            : 'No channels available. Make sure the Slack app has access to channels in this workspace.'}
        </Alert>
      ) : (
        <List sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {filteredChannels.map((channel) => {
            const isSelected = selectedChannels.some(c => c.id === channel.id);

            return (
              <ListItem
                key={channel.id}
                dense
                button
                onClick={() => handleChannelToggle(channel)}
                sx={{
                  '&:hover': { bgcolor: 'action.hover' },
                  bgcolor: isSelected ? 'action.selected' : 'transparent'
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': `channel-${channel.id}` }}
                  />
                </ListItemIcon>
                <ListItemIcon>
                  {getChannelIcon(channel)}
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  id={`channel-${channel.id}`}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        #{channel.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={getChannelTypeLabel(channel)}
                        color={channel.isPrivate ? 'warning' : 'default'}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      {channel.purpose && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {channel.purpose.length > 100
                            ? `${channel.purpose.substring(0, 100)}...`
                            : channel.purpose}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {channel.memberCount} member{channel.memberCount !== 1 ? 's' : ''}
                        {channel.lastActivity && (
                          ` • Last activity: ${new Date(channel.lastActivity).toLocaleDateString()}`
                        )}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}

export default SlackChannelSelector;